/**
 * P2PChat.js
 * WebRTC P2P 채팅 (시그널링 서버만 사용, 대화 기록 저장 없음)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  Chip,
  IconButton,
  Popover,
} from '@mui/material';
import { MessageCircle, PhoneOff, Send, RefreshCw, Smile } from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useMobileView } from '../contexts/MobileViewContext';
import apiConfig from '../config/api';
import { getUserFromToken } from '../utils/auth';
import EmployeeTreeSelect from '../components/EmployeeTreeSelect';
import axios from 'axios';

const DEFAULT_STUN = [{ urls: 'stun:stun.l.google.com:19302' }];

const P2PChat = () => {
  const { colors, isDark } = useCustomTheme();
  const { isMobileView } = useMobileView();

  const [targetCode, setTargetCode] = useState('');
  const [status, setStatus] = useState('idle'); // idle | connecting | waiting | connected | error
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [peerInfo, setPeerInfo] = useState(null);
  const [localUser, setLocalUser] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [allowedEmployeeCodes, setAllowedEmployeeCodes] = useState(null);
  const [isLoadingOrg, setIsLoadingOrg] = useState(false);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const [recentEmojis, setRecentEmojis] = useState([]);
  const [connectionId, setConnectionId] = useState('');
  const [loopbackEnabled, setLoopbackEnabled] = useState(false);

  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const dataChannelRef = useRef(null);
  const roomIdRef = useRef(null);
  const isInitiatorRef = useRef(false);

  const token = useMemo(() => localStorage.getItem('token') || '', []);

  useEffect(() => {
    const userFromToken = getUserFromToken(token);
    const employeeCode = userFromToken?.employee_code || localStorage.getItem('employeeCode') || '';
    const employeeName = localStorage.getItem('employeeName') || employeeCode || '사용자';
    setLocalUser({
      employee_code: employeeCode,
      employee_name: employeeName
    });
  }, [token]);

  const selectedEmployee = useMemo(() => {
    if (!targetCode || employees.length === 0) return null;
    return employees.find(emp => emp.employee_code === targetCode) || null;
  }, [employees, targetCode]);

  const fetchOrgTree = useCallback(async () => {
    if (!token) return;
    setIsLoadingOrg(true);
    try {
      const response = await axios.get(`${apiConfig.V1}/user/org-tree`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const employeeList = response.data?.employees || [];
      const departmentList = response.data?.departments || [];
      const allowedCodes = response.data?.employee_codes || [];
      const filteredAllowed = localUser?.employee_code
        ? allowedCodes.filter(code => code !== localUser.employee_code)
        : allowedCodes;
      setEmployees(employeeList);
      setDepartments(departmentList);
      setAllowedEmployeeCodes(filteredAllowed);
    } catch (error) {
      console.error('[P2PChat] 조직도 조회 실패:', error);
      setErrorMessage('조직도 정보를 불러오지 못했습니다.');
      setEmployees([]);
      setDepartments([]);
      setAllowedEmployeeCodes([]);
    } finally {
      setIsLoadingOrg(false);
    }
  }, [localUser?.employee_code, token]);

  useEffect(() => {
    fetchOrgTree();
  }, [fetchOrgTree]);

  const roomId = useMemo(() => {
    if (!localUser?.employee_code || !targetCode) return '';
    const sorted = [localUser.employee_code, targetCode].sort();
    return `p2p:${sorted.join(':')}`;
  }, [localUser?.employee_code, targetCode]);

  const wsUrl = useMemo(() => {
    const baseUrl = apiConfig.BASE || '';
    const baseWs = baseUrl
      ? baseUrl.replace(/^http/, 'ws')
      : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;
    return roomId && token
      ? `${baseWs}/ws/p2p-chat?room_id=${encodeURIComponent(roomId)}&token=${encodeURIComponent(token)}`
      : '';
  }, [roomId, token]);

  const appendMessage = useCallback((payload) => {
    setMessages((prev) => [...prev, payload]);
  }, []);

  const cleanupPeerConnection = useCallback(() => {
    if (dataChannelRef.current) {
      try {
        dataChannelRef.current.close();
      } catch (error) {
        // ignore
      }
      dataChannelRef.current = null;
    }
    if (pcRef.current) {
      try {
        pcRef.current.close();
      } catch (error) {
        // ignore
      }
      pcRef.current = null;
    }
    isInitiatorRef.current = false;
  }, []);

  const cleanupLoopback = useCallback(() => {
    setLoopbackEnabled(false);
  }, []);

  const cleanupSocket = useCallback(() => {
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch (error) {
        // ignore
      }
      wsRef.current = null;
    }
  }, []);

  const resetConnection = useCallback(() => {
    cleanupPeerConnection();
    cleanupSocket();
    cleanupLoopback();
    setPeerInfo(null);
    setStatus('idle');
  }, [cleanupPeerConnection, cleanupLoopback, cleanupSocket]);

  const shouldInitiateCall = useCallback((myCode, peerCode, myConnId, peerConnId) => {
    if (!myCode || !peerCode) return false;
    if (myCode === peerCode) {
      if (!myConnId || !peerConnId) return false;
      return myConnId.localeCompare(peerConnId) < 0;
    }
    return myCode.localeCompare(peerCode) < 0;
  }, []);

  const setupDataChannel = useCallback((channel) => {
    dataChannelRef.current = channel;
    channel.onopen = () => {
      setStatus('connected');
    };
    channel.onclose = () => {
      setStatus('waiting');
    };
    channel.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        appendMessage({
          sender: peerInfo?.employee_code || 'peer',
          senderName: peerInfo?.employee_name || '상대',
          text: payload.text,
          timestamp: payload.timestamp || new Date().toISOString(),
          isMine: false,
        });
      } catch (error) {
        appendMessage({
          sender: peerInfo?.employee_code || 'peer',
          senderName: peerInfo?.employee_name || '상대',
          text: event.data,
          timestamp: new Date().toISOString(),
          isMine: false,
        });
      }
    };
  }, [appendMessage, peerInfo?.employee_code]);

  const ensurePeerConnection = useCallback((isInitiator) => {
    if (pcRef.current) {
      return pcRef.current;
    }

    const pc = new RTCPeerConnection({ iceServers: DEFAULT_STUN });
    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current) {
        wsRef.current.send(JSON.stringify({
          type: 'ice',
          payload: event.candidate
        }));
      }
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        setStatus('connected');
      } else if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        setStatus('waiting');
      }
    };

    if (isInitiator) {
      const channel = pc.createDataChannel('p2p-chat');
      setupDataChannel(channel);
    } else {
      pc.ondatachannel = (event) => {
        setupDataChannel(event.channel);
      };
    }

    pcRef.current = pc;
    return pc;
  }, [setupDataChannel]);

  const sendOffer = useCallback(async () => {
    const pc = ensurePeerConnection(true);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    if (wsRef.current) {
      wsRef.current.send(JSON.stringify({
        type: 'offer',
        payload: offer
      }));
    }
  }, [ensurePeerConnection]);

  const handleOffer = useCallback(async (payload) => {
    const pc = ensurePeerConnection(false);
    await pc.setRemoteDescription(new RTCSessionDescription(payload));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    if (wsRef.current) {
      wsRef.current.send(JSON.stringify({
        type: 'answer',
        payload: answer
      }));
    }
  }, [ensurePeerConnection]);

  const handleAnswer = useCallback(async (payload) => {
    if (!pcRef.current) return;
    await pcRef.current.setRemoteDescription(new RTCSessionDescription(payload));
  }, []);

  const handleIce = useCallback(async (payload) => {
    if (!pcRef.current) return;
    try {
      await pcRef.current.addIceCandidate(new RTCIceCandidate(payload));
    } catch (error) {
      // ignore
    }
  }, []);

  const maybeStartCall = useCallback((peerCode, peerConnId) => {
    if (!localUser?.employee_code || !peerCode) return;
    if (pcRef.current) return;

    const shouldStart = shouldInitiateCall(localUser.employee_code, peerCode, connectionId, peerConnId);
    isInitiatorRef.current = shouldStart;
    if (shouldStart) {
      sendOffer().catch(() => {
        setErrorMessage('P2P 연결을 시작하지 못했습니다.');
        setStatus('error');
      });
    } else {
      setStatus('waiting');
    }
  }, [connectionId, localUser?.employee_code, sendOffer, shouldInitiateCall]);

  const handleSocketMessage = useCallback((event) => {
    const message = JSON.parse(event.data);

    switch (message.type) {
      case 'joined':
        if (message.user?.connection_id) {
          setConnectionId(message.user.connection_id);
        }
        if (message.peers && message.peers.length > 0) {
          const peer = message.peers[0];
          setPeerInfo(peer);
          maybeStartCall(peer.employee_code, peer.connection_id);
        } else {
          setStatus('waiting');
        }
        break;
      case 'peer-joined':
        setPeerInfo(message.user);
        maybeStartCall(message.user?.employee_code, message.user?.connection_id);
        break;
      case 'peer-left':
        setPeerInfo(null);
        cleanupPeerConnection();
        setStatus('waiting');
        break;
      case 'room-full':
        setErrorMessage(message.message || '방이 가득 찼습니다.');
        setStatus('error');
        cleanupSocket();
        break;
      case 'offer':
        handleOffer(message.payload);
        break;
      case 'answer':
        handleAnswer(message.payload);
        break;
      case 'ice':
        handleIce(message.payload);
        break;
      default:
        break;
    }
  }, [cleanupPeerConnection, cleanupSocket, handleAnswer, handleIce, handleOffer, maybeStartCall]);

  const connect = useCallback(() => {
    if (typeof RTCPeerConnection === 'undefined') {
      setErrorMessage('이 브라우저는 WebRTC를 지원하지 않습니다.');
      setStatus('error');
      return;
    }

    if (!localUser?.employee_code) {
      setErrorMessage('로그인 정보가 없습니다. 다시 로그인해주세요.');
      setStatus('error');
      return;
    }

    if (!targetCode) {
      setErrorMessage('대상 직원 코드를 입력해주세요.');
      setStatus('error');
      return;
    }

    if (targetCode === localUser.employee_code && process.env.NODE_ENV !== 'development') {
      setErrorMessage('본인과는 연결할 수 없습니다.');
      setStatus('error');
      return;
    }

    if (targetCode === localUser.employee_code && loopbackEnabled) {
      setErrorMessage('');
      setPeerInfo({
        employee_code: localUser.employee_code,
        employee_name: `${localUser.employee_name} (루프백)`
      });
      setStatus('connected');
      return;
    }

    if (!wsUrl) {
      setErrorMessage('WebSocket URL을 만들 수 없습니다.');
      setStatus('error');
      return;
    }

    setErrorMessage('');
    setMessages([]);
    setStatus('connecting');
    roomIdRef.current = roomId;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({
        type: 'join',
        room_id: roomIdRef.current
      }));
    };
    ws.onmessage = handleSocketMessage;
    ws.onerror = () => {
      setErrorMessage('시그널링 서버 연결에 실패했습니다.');
      setStatus('error');
    };
    ws.onclose = () => {
      cleanupPeerConnection();
      if (status !== 'idle') {
        setStatus('idle');
      }
    };
  }, [cleanupPeerConnection, handleSocketMessage, localUser?.employee_code, roomId, targetCode, wsUrl, status]);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'leave' }));
      }
    }
    resetConnection();
  }, [resetConnection]);

  const sendMessage = useCallback(() => {
    if (loopbackEnabled && targetCode === localUser?.employee_code) {
      const trimmed = inputMessage.trim();
      if (!trimmed) return;
      const payload = {
        text: trimmed,
        timestamp: new Date().toISOString()
      };
      appendMessage({
        sender: localUser?.employee_code || 'me',
        senderName: localUser?.employee_name || '나',
        text: trimmed,
        timestamp: payload.timestamp,
        isMine: true,
      });
      appendMessage({
        sender: localUser?.employee_code || 'me',
        senderName: `${localUser?.employee_name || '나'} (루프백)`,
        text: trimmed,
        timestamp: payload.timestamp,
        isMine: false,
      });
      setInputMessage('');
      return;
    }

    if (!dataChannelRef.current || dataChannelRef.current.readyState !== 'open') {
      setErrorMessage('데이터 채널이 연결되지 않았습니다.');
      return;
    }
    const trimmed = inputMessage.trim();
    if (!trimmed) return;

    const payload = {
      text: trimmed,
      timestamp: new Date().toISOString()
    };
    dataChannelRef.current.send(JSON.stringify(payload));
    appendMessage({
      sender: localUser?.employee_code || 'me',
      senderName: localUser?.employee_name || '나',
      text: trimmed,
      timestamp: payload.timestamp,
      isMine: true,
    });
    setInputMessage('');
  }, [appendMessage, inputMessage, localUser?.employee_code]);

  const emojiList = [
    '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '😉', '😊', '😇',
    '😍', '😘', '😗', '😙', '😚', '🤗', '🤔', '😐', '😑', '😶', '🙄', '😏',
    '😴', '😪', '😋', '😛', '😜', '😝', '🤤', '😒', '😓', '😔', '😕', '🙃',
    '😲', '😳', '🥺', '😤', '😠', '😡', '🤬', '😱', '😨', '😰', '😢', '😭',
    '🤝', '👍', '👎', '👏', '🙏', '💪', '🙌', '🫶', '🤟', '🤞', '✋', '👌',
    '🔥', '💡', '✨', '⭐', '🌟', '💥', '✅', '❗', '❓', '💬', '🗯️', '📌',
    '🚀', '🎉', '🎊', '🎯', '🎁', '❤️', '🧡', '💛', '💚', '💙', '💜', '🤍',
    '☕', '🍰', '🍔', '🍕', '🍺', '🥤', '🌈', '🌞', '🌙', '🌧️', '⚡', '🎵'
  ];

  const handleOpenEmojiPicker = (event) => {
    setEmojiAnchorEl(event.currentTarget);
  };

  const handleCloseEmojiPicker = () => {
    setEmojiAnchorEl(null);
  };

  const handleEmojiSelect = (emoji) => {
    setInputMessage((prev) => `${prev}${emoji}`);
    setRecentEmojis((prev) => {
      const next = [emoji, ...prev.filter((item) => item !== emoji)].slice(0, 12);
      localStorage.setItem('p2p_recent_emojis', JSON.stringify(next));
      return next;
    });
    setEmojiAnchorEl(null);
  };

  useEffect(() => {
    const saved = localStorage.getItem('p2p_recent_emojis');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setRecentEmojis(parsed);
        }
      } catch (error) {
        // ignore
      }
    }
  }, []);

  const filteredEmojis = useMemo(() => emojiList, [emojiList]);

  useEffect(() => {
    return () => {
      resetConnection();
    };
  }, [resetConnection]);

  const statusLabel = {
    idle: '대기',
    connecting: '연결 중',
    waiting: '상대방 대기',
    connected: '연결됨',
    error: '오류'
  }[status];

  return (
    <Box sx={{ p: isMobileView ? 2 : 3, minHeight: '100vh', backgroundColor: colors.primaryBg }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: colors.primaryText, fontSize: isMobileView ? '1.5rem' : '20px' }}>
          💬 P2P 채팅
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 900, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Card sx={{ backgroundColor: colors.secondaryBg, border: `1px solid ${colors.primaryBorder}` }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                연결 설정
              </Typography>
              <Chip
                label={`상태: ${statusLabel}`}
                size="small"
                sx={{
                  backgroundColor: status === 'connected' ? colors.accent : colors.tertiaryBg,
                  color: status === 'connected' ? '#fff' : colors.secondaryText
                }}
              />
            </Box>
            <Divider sx={{ my: 2, borderColor: colors.primaryBorder }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="내 직원 코드"
                value={localUser?.employee_code || ''}
                size="small"
                InputProps={{ readOnly: true }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" sx={{ color: colors.secondaryText }}>
                  조직도에서 대화 상대 선택
                </Typography>
                  <EmployeeTreeSelect
                  employees={employees}
                  departments={departments}
                  selectedEmployees={targetCode ? [targetCode] : []}
                  onSelectionChange={(codes) => {
                    const code = codes[0] || '';
                    setTargetCode(code);
                    setErrorMessage('');
                  }}
                  multiple={false}
                  placeholder={isLoadingOrg ? '조직도 불러오는 중...' : '대화 상대 선택'}
                  allowedEmployeeCodes={allowedEmployeeCodes}
                />
                  {targetCode === localUser?.employee_code && process.env.NODE_ENV === 'development' && (
                    <Alert
                      severity="info"
                      action={
                        <Button
                          size="small"
                          onClick={() => {
                            setLoopbackEnabled(true);
                            setStatus('connected');
                            setPeerInfo({
                              employee_code: localUser.employee_code,
                              employee_name: `${localUser.employee_name} (루프백)`
                            });
                          }}
                        >
                          루프백 모드
                        </Button>
                      }
                    >
                      개발환경에서만 자기 자신과 루프백 테스트가 가능합니다.
                    </Alert>
                  )}
                <TextField
                  label="선택된 상대"
                  value={selectedEmployee ? `${selectedEmployee.employee_name} (${selectedEmployee.employee_code})` : ''}
                  size="small"
                  InputProps={{ readOnly: true }}
                />
                {allowedEmployeeCodes && allowedEmployeeCodes.length === 0 && (
                  <Alert severity="warning">
                    현재 선택 가능한 대화 상대가 없습니다.
                  </Alert>
                )}
              </Box>
              {peerInfo && (
                <Alert severity="info">
                  연결 대상: {peerInfo.employee_name || peerInfo.employee_code}
                </Alert>
              )}
              {errorMessage && (
                <Alert severity="error">
                  {errorMessage}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                  variant="contained"
                  startIcon={<MessageCircle size={16} />}
                  sx={{ backgroundColor: colors.accent }}
                  onClick={connect}
                disabled={status === 'connecting' || status === 'connected'}
                >
                  연결 시작
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PhoneOff size={16} />}
                  onClick={disconnect}
                  disabled={status === 'idle'}
                >
                  연결 종료
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshCw size={16} />}
                  onClick={() => setMessages([])}
                >
                  대화 지우기
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ backgroundColor: colors.secondaryBg, border: `1px solid ${colors.primaryBorder}` }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 2 }}>
              대화
            </Typography>
            <Box
              sx={{
                backgroundColor: colors.tertiaryBg,
                borderRadius: 1,
                border: `1px solid ${colors.primaryBorder}`,
                p: 2,
                minHeight: 240,
                maxHeight: 360,
                overflowY: 'auto'
              }}
            >
              {messages.length === 0 ? (
                <Typography variant="body2" sx={{ color: colors.secondaryText }}>
                  아직 대화가 없습니다. 연결 후 메시지를 보내보세요.
                </Typography>
              ) : (
                messages.map((msg, idx) => (
                  <Box
                    key={`${msg.timestamp}-${idx}`}
                    sx={{
                      mb: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: msg.isMine ? 'flex-end' : 'flex-start'
                    }}
                  >
                    <Typography variant="caption" sx={{ color: colors.secondaryText }}>
                      {(msg.isMine ? '나' : (msg.senderName || msg.sender))} · {new Date(msg.timestamp).toLocaleTimeString()}
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.5,
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        maxWidth: '75%',
                        backgroundColor: msg.isMine
                          ? (isDark ? 'rgba(138, 124, 255, 0.2)' : 'rgba(138, 124, 255, 0.12)')
                          : colors.primaryBg,
                        border: `1px solid ${colors.primaryBorder}`,
                        color: colors.primaryText,
                        alignSelf: msg.isMine ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <Typography variant="body2" sx={{ color: colors.primaryText, wordBreak: 'break-word' }}>
                        {msg.text}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
            <Divider sx={{ my: 2, borderColor: colors.primaryBorder }} />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                placeholder="메시지를 입력하세요"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    sendMessage();
                  }
                }}
                size="small"
              />
              <IconButton
                aria-label="이모지 선택"
                onClick={handleOpenEmojiPicker}
                sx={{
                  border: `1px solid ${colors.primaryBorder}`,
                  borderRadius: 1,
                  color: colors.secondaryText,
                  '&:hover': {
                    borderColor: colors.accent,
                    color: colors.accent
                  }
                }}
              >
                <Smile size={18} />
              </IconButton>
              <Button
                variant="contained"
                startIcon={<Send size={16} />}
                sx={{ backgroundColor: colors.accent }}
                onClick={sendMessage}
                disabled={status !== 'connected'}
              >
              </Button>
              <Popover
                open={Boolean(emojiAnchorEl)}
                anchorEl={emojiAnchorEl}
                onClose={handleCloseEmojiPicker}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                  sx: {
                    p: 1,
                    backgroundColor: colors.secondaryBg,
                    border: `1px solid ${colors.primaryBorder}`,
                    borderRadius: 1,
                    width: 260
                  }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {recentEmojis.length > 0 && (
                    <Box>
                      <Typography variant="caption" sx={{ color: colors.secondaryText }}>
                        최근 사용
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.5, mt: 0.5 }}>
                        {recentEmojis.map((emoji) => (
                          <IconButton
                            key={`recent-${emoji}`}
                            size="small"
                            onClick={() => handleEmojiSelect(emoji)}
                            sx={{ fontSize: '18px' }}
                          >
                            {emoji}
                          </IconButton>
                        ))}
                      </Box>
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(8, 1fr)',
                      gap: 0.5,
                      maxHeight: 200,
                      overflowY: 'auto',
                      pr: 0.5
                    }}
                  >
                    {filteredEmojis.map((emoji) => (
                      <IconButton
                        key={emoji}
                        size="small"
                        onClick={() => handleEmojiSelect(emoji)}
                        sx={{ fontSize: '18px' }}
                      >
                        {emoji}
                      </IconButton>
                    ))}
                  </Box>
                </Box>
              </Popover>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default P2PChat;
